interface ParrotSpinnerProps {
  /**
   * Chakra-style size token (xs–xl) or an explicit pixel number. Kept for
   * backwards compatibility with existing callers.
   */
  size?: string | number;
}

// iOS parity: client-native LoadingSpinner renders a native <ActivityIndicator>
// — the segmented 12-bar radial indicator, not a single rotating ring. We
// reproduce that shape on web with 12 fading bars rotating around a center,
// tinted with var(--color-blue).
const SIZE_MAP: Record<string, number> = {
  xs: 16,
  sm: 20,
  md: 28,
  lg: 36,
  xl: 48,
};

const BAR_COUNT = 12;

export default function ParrotSpinner({ size = "xl" }: ParrotSpinnerProps) {
  const dimension =
    typeof size === "number" ? size : SIZE_MAP[size] ?? SIZE_MAP.xl;
  // Bar geometry derived from the overall dimension so it scales cleanly.
  const barWidth = Math.max(2, Math.round(dimension * 0.09));
  const barHeight = Math.round(dimension * 0.28);
  const radius = dimension / 2;

  return (
    <div
      role="status"
      aria-label="Loading"
      style={{
        width: dimension,
        height: dimension,
        position: "relative",
        display: "inline-block",
      }}
    >
      <style>
        {`@keyframes parrot-spinner-fade { 0% { opacity: 1; } 100% { opacity: 0.15; } }`}
      </style>
      {Array.from({ length: BAR_COUNT }).map((_, i) => {
        const rotation = (360 / BAR_COUNT) * i;
        // Stagger each bar's fade so the highlight sweeps around the ring.
        const delay = -(BAR_COUNT - i) * (1 / BAR_COUNT) + "s";
        return (
          <span
            key={i}
            style={{
              position: "absolute",
              top: 0,
              left: radius - barWidth / 2,
              width: barWidth,
              height: barHeight,
              borderRadius: barWidth,
              backgroundColor: "var(--color-blue)",
              transformOrigin: `${barWidth / 2}px ${radius}px`,
              transform: `rotate(${rotation}deg)`,
              animation: "parrot-spinner-fade 1s linear infinite",
              animationDelay: delay,
            }}
          />
        );
      })}
    </div>
  );
}
